import useNeveForm from 'components/forms/NeveForm';
import strings from 'strings/strings.json';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import React from 'react';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { CreateActivityFormProps } from './shared/CreateActivityFormProps';
import useUpsertRequest from 'hooks/useUpsertRequest';
import { FlexColSm, Headline3 } from 'components/Elements';
import TextField from 'components/forms/TextField';
import Button from 'components/buttons/Button';
import AddIcon from 'assets/icons/controls/AddIcon';
import { useFieldArray, useWatch } from 'react-hook-form';
import { ActivityType } from 'models/Activity';
import { ActivityFormCard } from '../shared/ActivityFormCard';
import {
    QuestionnaireInput,
    CreateQuestionnaireRequest,
    QuestionCard,
    ButtonContainer,
} from '../shared/SharedQuestionnaireComponents';
import BaseDetailsForm from '../shared/DetailsForm';
import TextAreaField from 'components/forms/TextAreaField';

export default function CreateQuestionnaireForm({
    returnToModulesList,
    moduleId,
}: CreateActivityFormProps): JSX.Element {
    const { refetchCourse } = React.useContext(BuildCourseContext);
    const [error, setError] = React.useState('');
    const { modifyData: upsertQuestionnaire, loading: submitting } = useUpsertRequest(`activities/questionnaire`);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
    } = useNeveForm<QuestionnaireInput>({
        title: '',
        description: '',
        durationInMins: null,
        questions: [],
    });

    const watchedQuestions = useWatch({
        control,
        name: 'questions',
    });

    React.useEffect(() => {
        setError('');
    }, [watchedQuestions]);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'questions',
    });

    function addNewQuestion() {
        append({ question: '', hint: '' });
        setError('');
    }

    function removeQuestion(index: number) {
        remove(index);
    }

    async function submit(formData: QuestionnaireInput) {
        setError('');

        if (!formData.questions.length) {
            setError(strings.questionnaireForm.error.addQuestion);
            return;
        }

        const request: CreateQuestionnaireRequest = {
            title: formData.title,
            description: formData.description,
            durationInMins: formData.durationInMins,
            questions: formData.questions,
            moduleId: moduleId,
        };

        const response = await upsertQuestionnaire(request);
        const { errors } = response;

        if (errors) {
            setError(strings.questionnaireForm.error.create);
            return;
        }

        await refetchCourse();
        returnToModulesList();
    }

    const Questions = (
        <FlexColSm>
            <Headline3>{strings.questionnaireForm.questionsTitle}</Headline3>
            {fields.map((question, index) => {
                let questionErrorMessage = '';
                let hintTextErrorMessage = '';

                if (errors.questions && errors.questions[index] !== undefined) {
                    questionErrorMessage = errors.questions[index]?.question?.message ?? '';
                    hintTextErrorMessage = errors.questions[index]?.hint?.message ?? '';
                }

                return (
                    <QuestionCard key={index}>
                        <TextField
                            fieldName={`question-${index}`}
                            labelText={strings.questionnaireForm.questionInputLabel}
                            inputAria={strings.questionnaireForm.questionInputAria}
                            errorMessage={questionErrorMessage}
                            required
                            {...register(`questions.${index}.question`, {
                                required: {
                                    value: true,
                                    message: requiredFieldErrorMessage(strings.questionnaireForm.questionInputLabel),
                                },
                                maxLength: {
                                    value: 2000,
                                    message: createErrorMessage(
                                        strings.questionnaireForm.questionInputLabel,
                                        fieldErrorCodes.maxLength,
                                    ),
                                },
                            })}
                        />

                        <TextAreaField
                            fieldName={strings.questionnaireForm.hintInputLabel}
                            errorMessage={hintTextErrorMessage}
                            aria={strings.questionnaireForm.hintInputAria}
                            maxLength={2000}
                            {...register(`questions.${index}.hint`, {
                                maxLength: {
                                    value: 2000,
                                    message: createErrorMessage(
                                        strings.questionnaireForm.hintInputLabel,
                                        fieldErrorCodes.maxLength,
                                    ),
                                },
                            })}
                        />
                        <Button
                            title={strings.questionnaireForm.removeQuestionButton}
                            aria={strings.questionnaireForm.removeQuestionButtonAria}
                            colour={'BLACK'}
                            onClick={() => removeQuestion(index)}
                            disabled={submitting}
                        />
                    </QuestionCard>
                );
            })}
            <ButtonContainer>
                <Button
                    fullwidth
                    title={strings.questionnaireForm.addQuestionButton}
                    aria={strings.questionnaireForm.addQuestionButtonAria}
                    icon={<AddIcon />}
                    onClick={() => addNewQuestion()}
                    disabled={submitting}
                />
            </ButtonContainer>
        </FlexColSm>
    );

    return (
        <ActivityFormCard
            title={strings.questionnaireForm.title.create}
            onSubmit={handleSubmit(submit)}
            pageOne={<BaseDetailsForm register={register} errors={errors} autoFocus />}
            pageTwo={Questions}
            activityType={ActivityType.QUESTIONNAIRE}
            error={error}
            submitting={submitting}
        />
    );
}
